/* eslint-disable lingui/no-unlocalized-strings -- closed machine telemetry schema; no user-facing copy */
import { ApolloLink, HttpLink, Observable, type Operation } from "@apollo/client";

import type {
  RecoveryDecisionObservation,
  RecoveryTrigger,
} from "./auth-recovery";
import type { ApiConnectivityObservation } from "./api-connectivity";

const WINDOW_MS = 60 * 60 * 1_000;
const RING_SIZE = 9;
const MAX_BREADCRUMBS = 30;
const MAX_COUNTER = 9_999;
const MAX_DURATION_MS = 60_000;

const EVENT_CAPS = {
  recoveryFailure: 6,
  recoverySuccess: 2,
  refreshSuccess: 2,
  httpSuccess: 2,
  outageRecovery: 2,
} as const;

type EventBudget = keyof typeof EVENT_CAPS;
export type RefreshSource =
  | "http_auth"
  | "ws_connection"
  | "subscription_recovery"
  | "reachability_probe";
type RefreshFinish =
  | "parsed_success"
  | "http_rejected"
  | "parse_failure"
  | "network"
  | "abort"
  | "timeout"
  | "unknown";
type HttpFinish =
  | "completed"
  | "http_error"
  | "parse_failure"
  | "network"
  | "abort"
  | "cancelled"
  | "unknown";

type DiagnosticRecord = { sequence: number; kind: string } & Record<
  string,
  unknown
>;

type Counter = { observed: number; emitted: number; suppressed: number };
type DiagnosticSummary = Record<string, string | number | boolean>;

export interface RefreshResponseMetadata {
  status: number;
  responseType:
    | "basic"
    | "cors"
    | "opaque"
    | "opaqueredirect"
    | "error"
    | "default"
    | "none"
    | "unreadable"
    | "unknown";
  contentType: "json" | "html" | "other" | "missing" | "unreadable";
  challenge: "challenge" | "not_challenge" | "unreadable";
  rayId: string;
  cacheStatus: string;
  ageBucket: string;
  cacheControl:
    | "missing"
    | "unreadable"
    | {
        noStore: boolean;
        noCache: boolean;
        private: boolean;
        maxAge: boolean;
      };
}

export interface RefreshDiagnosticSnapshot {
  kind: "refresh";
  refreshId: string;
  source: RefreshSource;
  operationName: string;
  recoveryAttemptId: string;
  durationMs: number;
  result: RefreshFinish;
  applicationClassification: "invalid" | "transient" | "not_applied";
  response: RefreshResponseMetadata;
}

export interface BrowserDiagnosticSink {
  captureMessage: (
    message: string,
    context: {
      level: "info";
      fingerprint: string[];
      contexts: { browserConnectivity: Record<string, unknown> };
    },
  ) => unknown;
  addBreadcrumb: (breadcrumb: {
    category: "browser-connectivity";
    level: "info" | "warning";
    message: string;
    data: Record<string, unknown>;
  }) => unknown;
}

export interface RefreshDiagnosticContext {
  source: RefreshSource;
  operationName?: string;
  recoveryAttemptId?: string;
  onFinished?: (snapshot: RefreshDiagnosticSnapshot) => void;
}

export interface BrowserConnectivityDiagnostics {
  startRefresh: (context: RefreshDiagnosticContext) => {
    id: string;
    noteResponse: (response: Response) => void;
    finish: (
      result: RefreshFinish,
      applicationClassification?: "invalid" | "transient",
    ) => RefreshDiagnosticSnapshot;
  };
  createRecoveryAttempt: (trigger: RecoveryTrigger) => {
    attemptId: string;
    trigger: RecoveryTrigger;
  };
  observeRecoveryDecision: (
    observation: RecoveryDecisionObservation<RefreshDiagnosticSnapshot>,
  ) => void;
  observeConnectivity: (observation: ApiConnectivityObservation) => void;
  outageWarningContext: () => Record<string, unknown>;
  startHttp: (
    operation: Operation,
    input: RequestInfo | URL,
    init: RequestInit | undefined,
  ) => {
    noteResponse: (response: Response) => void;
    noteFetchFailure: (error: unknown) => void;
    finish: (result: HttpFinish) => void;
  };
}

function increment(value: number): number {
  return Math.min(MAX_COUNTER, value + 1);
}

function nextIdentifier(value: number): number {
  return value >= Number.MAX_SAFE_INTEGER ? 1 : value + 1;
}

function clampDuration(value: number): number {
  if (!Number.isFinite(value)) return MAX_DURATION_MS;
  return Math.max(0, Math.min(MAX_DURATION_MS, Math.round(value)));
}

function safeOperationName(value: string | undefined): string {
  return value && /^[A-Za-z_][A-Za-z0-9_]{0,63}$/.test(value)
    ? value
    : "unknown";
}

function safeAttemptId(value: string | undefined): string {
  return value && /^(?:attempt|refresh)-[0-9]{1,15}$/.test(value)
    ? value
    : "none";
}

function classifyFailure(error: unknown): "network" | "abort" | "timeout" | "unknown" {
  if (error instanceof Error) {
    if (error.name === "AbortError") return "abort";
    if (error.name === "TimeoutError") return "timeout";
    if (error instanceof TypeError) return "network";
  }
  return "unknown";
}

function unreadableResponseMetadata(): RefreshResponseMetadata {
  return {
    status: 0,
    responseType: "unreadable",
    contentType: "unreadable",
    challenge: "unreadable",
    rayId: "absent",
    cacheStatus: "unreadable",
    ageBucket: "unreadable",
    cacheControl: "unreadable",
  };
}

function responseMetadata(response: Response): RefreshResponseMetadata {
  let contentType: RefreshResponseMetadata["contentType"] = "unreadable";
  let challenge: RefreshResponseMetadata["challenge"] = "unreadable";
  let rayId = "absent";
  let cacheStatus = "unreadable";
  let ageBucket = "unreadable";
  let cacheControl: RefreshResponseMetadata["cacheControl"] = "unreadable";
  try {
    const rawContentType = response.headers.get("content-type");
    contentType = rawContentType === null
      ? "missing"
      : rawContentType.toLowerCase().includes("application/json")
        ? "json"
        : rawContentType.toLowerCase().includes("text/html")
          ? "html"
          : "other";

    challenge = response.headers.get("cf-mitigated")?.toLowerCase() === "challenge"
      ? "challenge"
      : "not_challenge";

    const rawRay = response.headers.get("cf-ray");
    rayId = rawRay && /^[A-Fa-f0-9]{16,32}(?:-[A-Z]{3})?$/.test(rawRay)
      ? rawRay
      : rawRay === null
        ? "absent"
        : "invalid";

    const rawCacheStatus = response.headers.get("cf-cache-status")?.toUpperCase();
    cacheStatus = rawCacheStatus === undefined
      ? "missing"
      : ["HIT", "MISS", "BYPASS", "DYNAMIC", "EXPIRED", "REVALIDATED", "STALE", "UPDATING"].includes(rawCacheStatus)
        ? rawCacheStatus.toLowerCase()
        : "other";

    const rawAge = response.headers.get("age");
    if (rawAge === null) ageBucket = "missing";
    else {
      const age = Number(rawAge);
      ageBucket = !Number.isFinite(age) || age < 0
        ? "invalid"
        : age < 60
          ? "lt_minute"
          : age < 3_600
            ? "lt_hour"
            : "ge_hour";
    }

    const rawCacheControl = response.headers.get("cache-control");
    if (rawCacheControl === null) cacheControl = "missing";
    else {
      const directives = rawCacheControl.toLowerCase();
      cacheControl = {
        noStore: directives.includes("no-store"),
        noCache: directives.includes("no-cache"),
        private: directives.includes("private"),
        maxAge: /(?:^|,)\s*(?:s-maxage|max-age)\s*=/.test(directives),
      };
    }
  } catch {
    // Headers can be opaque or supplied by a hostile test double. Keep enums only.
  }

  const responseType = ["basic", "cors", "opaque", "opaqueredirect", "error", "default"].includes(response.type)
    ? (response.type as RefreshResponseMetadata["responseType"])
    : "unknown";

  return {
    status: Number.isInteger(response.status) && response.status >= 100 && response.status <= 599
      ? response.status
      : 0,
    responseType,
    contentType,
    challenge,
    rayId,
    cacheStatus,
    ageBucket,
    cacheControl,
  };
}

function createIncidentId(generator?: () => string): string {
  try {
    const supplied = generator?.();
    if (supplied && /^incident-[a-f0-9]{16,32}$/.test(supplied)) return supplied;
  } catch {
    // Fall through to browser randomness.
  }
  try {
    const words = new Uint32Array(3);
    globalThis.crypto.getRandomValues(words);
    return `incident-${Array.from(words, (word) => word.toString(16).padStart(8, "0")).join("")}`;
  } catch {
    const part = () => Math.floor(Math.random() * 0xffff_ffff)
      .toString(16)
      .padStart(8, "0");
    return `incident-${part()}${part()}${part()}`;
  }
}

function endpointCategory(input: RequestInfo | URL): string {
  if (typeof input === "string") {
    return input.startsWith("/") ? "same_origin_graphql" : "configured_graphql";
  }
  if (input instanceof URL) {
    return "configured_graphql";
  }
  return "request_object";
}

function requestMetadata(
  operation: Operation,
  input: RequestInfo | URL,
  init: RequestInit | undefined,
): Record<string, unknown> {
  const knownHeaderNames = new Set([
    "accept",
    "apollo-require-preflight",
    "authorization",
    "baggage",
    "content-type",
    "sentry-trace",
    "x-nspr-app",
    "x-nspr-app-version",
  ]);
  const suppliedKnown: string[] = [];
  let unknownHeaderCount = 0;
  let applicationHeadersReadable = true;
  try {
    const attemptedHeaders = new Headers(init?.headers);
    const applicationHeaders = new Headers(operation.getContext().headers);
    applicationHeaders.forEach((_value, name) => {
      const normalized = name.toLowerCase();
      if (!attemptedHeaders.has(normalized)) return;
      if (knownHeaderNames.has(normalized)) suppliedKnown.push(normalized);
      else unknownHeaderCount = increment(unknownHeaderCount);
    });
  } catch {
    applicationHeadersReadable = false;
  }

  const method = init?.method?.toUpperCase();
  const credentials = init?.credentials;
  return {
    endpoint: endpointCategory(input),
    method: method === "POST" ? "post" : method === "GET" ? "get" : method ? "other" : "default",
    credentials: credentials === "omit" || credentials === "include" || credentials === "same-origin"
      ? credentials
      : credentials
        ? "other"
        : "default",
    applicationHeaderNames: suppliedKnown.sort(),
    unknownHeaderCount,
    applicationHeadersReadable,
  };
}

function safeTrigger(trigger: RecoveryTrigger): Record<string, unknown> {
  if (trigger.kind === "ws_close") {
    return {
      kind: trigger.kind,
      closeCode: Number.isInteger(trigger.closeCode)
        ? Math.max(0, Math.min(4_999, trigger.closeCode))
        : 0,
      streak: Number.isInteger(trigger.streak)
        ? Math.max(0, Math.min(99, trigger.streak))
        : 0,
    };
  }
  return { kind: trigger.kind };
}

export function createBrowserConnectivityDiagnostics(args: {
  sink: BrowserDiagnosticSink;
  now?: () => number;
  random?: () => number;
  incidentId?: () => string;
}): BrowserConnectivityDiagnostics {
  const now = args.now ?? (() => Date.now());
  const random = args.random ?? (() => Math.random());
  let windowStartedAt = now();
  let sequence = 0;
  let refreshId = 0;
  let recoveryId = 0;
  let activeIncident: string | undefined;
  let ring: DiagnosticRecord[] = [];
  let breadcrumbCounter: Counter = { observed: 0, emitted: 0, suppressed: 0 };
  let counters = Object.fromEntries(
    Object.keys(EVENT_CAPS).map((key) => [key, { observed: 0, emitted: 0, suppressed: 0 }]),
  ) as Record<EventBudget, Counter>;

  function resetWindowIfNeeded() {
    const current = now();
    if (current - windowStartedAt < WINDOW_MS) return;
    windowStartedAt = current;
    ring = [];
    breadcrumbCounter = { observed: 0, emitted: 0, suppressed: 0 };
    counters = Object.fromEntries(
      Object.keys(EVENT_CAPS).map((key) => [key, { observed: 0, emitted: 0, suppressed: 0 }]),
    ) as Record<EventBudget, Counter>;
  }

  function nextSequence() {
    sequence = increment(sequence);
    return sequence;
  }

  function counterSnapshot(): Record<string, unknown> {
    return {
      ...Object.fromEntries(
        Object.entries(counters).map(([key, value]) => [key, { ...value }]),
      ),
      breadcrumbs: { ...breadcrumbCounter },
    };
  }

  function envelope(
    category: string,
    summary: DiagnosticSummary = {},
  ): Record<string, unknown> {
    return {
      schema: "browser-connectivity-v1",
      category,
      incidentId: activeIncident ?? "none",
      windowMinutes: 60,
      ...summary,
      // Sentry's default normalization depth collapses nested context records.
      // These values are already a closed, sanitized schema, so encode them as
      // bounded primitives that survive SDK preparation intact.
      countersJson: JSON.stringify(counterSnapshot()),
      sequenceJson: JSON.stringify(ring),
    };
  }

  function addBreadcrumb(record: DiagnosticRecord, level: "info" | "warning") {
    breadcrumbCounter.observed = increment(breadcrumbCounter.observed);
    if (breadcrumbCounter.emitted >= MAX_BREADCRUMBS) {
      breadcrumbCounter.suppressed = increment(breadcrumbCounter.suppressed);
      return;
    }
    breadcrumbCounter.emitted = increment(breadcrumbCounter.emitted);
    try {
      args.sink.addBreadcrumb({
        category: "browser-connectivity",
        level,
        message: `browser connectivity ${record.kind}`,
        data: { recordJson: JSON.stringify(record) },
      });
    } catch {
      // Diagnostics are fail-open by contract.
    }
  }

  function record(
    item: Omit<DiagnosticRecord, "sequence">,
    level: "info" | "warning" = "info",
  ) {
    resetWindowIfNeeded();
    const complete = { ...item, sequence: nextSequence() } as DiagnosticRecord;
    ring.push(complete);
    if (ring.length > RING_SIZE) ring.shift();
    addBreadcrumb(complete, level);
  }

  function emit(
    budget: EventBudget,
    message: string,
    fingerprint: string[],
    category: string,
    eligible = true,
    summary: DiagnosticSummary = {},
  ) {
    resetWindowIfNeeded();
    const counter = counters[budget];
    counter.observed = increment(counter.observed);
    if (!eligible || counter.emitted >= EVENT_CAPS[budget]) {
      counter.suppressed = increment(counter.suppressed);
      return;
    }
    counter.emitted = increment(counter.emitted);
    try {
      args.sink.captureMessage(message, {
        level: "info",
        fingerprint,
        contexts: { browserConnectivity: envelope(category, summary) },
      });
    } catch {
      // Diagnostics are fail-open by contract.
    }
  }

  function startRefresh(context: RefreshDiagnosticContext) {
    refreshId = nextIdentifier(refreshId);
    const id = `refresh-${refreshId}`;
    const startedAt = now();
    let response: RefreshResponseMetadata = {
      ...unreadableResponseMetadata(),
      responseType: "none",
    };
    let finished = false;
    let snapshot: RefreshDiagnosticSnapshot | undefined;
    return {
      id,
      noteResponse(value: Response) {
        try {
          response = responseMetadata(value);
        } catch {
          response = unreadableResponseMetadata();
        }
      },
      finish(
        result: RefreshFinish,
        applicationClassification?: "invalid" | "transient",
      ) {
        if (finished && snapshot) return snapshot;
        finished = true;
        snapshot = {
          kind: "refresh",
          refreshId: id,
          source: context.source,
          operationName: safeOperationName(context.operationName),
          recoveryAttemptId: safeAttemptId(context.recoveryAttemptId),
          durationMs: clampDuration(now() - startedAt),
          result,
          applicationClassification: applicationClassification ?? "not_applied",
          response: { ...response },
        };
        record({ ...snapshot }, result === "parsed_success" ? "info" : "warning");
        if (result === "parsed_success") {
          emit(
            "refreshSuccess",
            "browser diagnostics: refresh control",
            ["browser-connectivity-diagnostics", "refresh-control"],
            "refresh_control",
            random() < 0.1,
            {
              operationName: snapshot.operationName,
              refreshSource: snapshot.source,
              refreshResult: snapshot.result,
              responseStatus: snapshot.response.status,
              responseChallenge: snapshot.response.challenge,
            },
          );
        }
        return snapshot;
      },
    };
  }

  function createRecoveryAttempt(trigger: RecoveryTrigger) {
    recoveryId = nextIdentifier(recoveryId);
    return { attemptId: `attempt-${recoveryId}`, trigger };
  }

  function observeRecoveryDecision(
    observation: RecoveryDecisionObservation<RefreshDiagnosticSnapshot>,
  ) {
    const attemptId = safeAttemptId(observation.attempt.attemptId);
    record(
      {
        kind: "recovery_decision",
        attemptId,
        trigger: safeTrigger(observation.attempt.trigger),
        outcome: observation.outcome,
        decision: observation.decision,
        refresh: observation.refreshDiagnostic ?? null,
      },
      observation.decision === "reconnect" ? "info" : "warning",
    );
    const success = observation.decision === "reconnect";
    emit(
      success ? "recoverySuccess" : "recoveryFailure",
      // Sentry Dedupe ignores contexts; keep distinct safe attempts deliverable.
      `browser diagnostics: subscription auth recovery ${attemptId}`,
      ["browser-connectivity-diagnostics", "recovery-decision", observation.decision],
      "recovery_decision",
      true,
      {
        decision: observation.decision,
        recoveryOutcome: observation.outcome,
        triggerKind: observation.attempt.trigger.kind,
        refreshSource: observation.refreshDiagnostic?.source ?? "none",
        refreshResult: observation.refreshDiagnostic?.result ?? "none",
        responseStatus: observation.refreshDiagnostic?.response.status ?? 0,
        responseChallenge: observation.refreshDiagnostic?.response.challenge ?? "unreadable",
      },
    );
  }

  function observeConnectivity(observation: ApiConnectivityObservation) {
    if (observation.kind === "outage_started") {
      activeIncident = createIncidentId(args.incidentId);
    }
    record({
      kind: observation.kind,
      operationName: safeOperationName("operationName" in observation ? observation.operationName : undefined),
      reason: "reason" in observation ? observation.reason : "none",
      result: "result" in observation ? observation.result : "none",
      generation: "generation" in observation
        ? Math.max(0, Math.min(MAX_COUNTER, observation.generation))
        : 0,
      incidentId: activeIncident ?? "none",
    }, observation.kind === "outage_started" ? "warning" : "info");
    if (observation.kind === "outage_recovered" && activeIncident) {
      emit(
        "outageRecovery",
        "browser diagnostics: HTTP transport recovered",
        ["browser-connectivity-diagnostics", "http-transport-recovered"],
        "outage_recovery",
      );
      activeIncident = undefined;
    }
  }

  function startHttp(
    operation: Operation,
    input: RequestInfo | URL,
    init: RequestInit | undefined,
  ) {
    const startedAt = now();
    const request = requestMetadata(operation, input, init);
    let response: RefreshResponseMetadata = {
      ...unreadableResponseMetadata(),
      responseType: "none",
    };
    let fetchFailure: "network" | "abort" | "timeout" | "unknown" | undefined;
    let finished = false;
    return {
      noteResponse(value: Response) {
        try {
          response = responseMetadata(value);
        } catch {
          response = unreadableResponseMetadata();
        }
      },
      noteFetchFailure(error: unknown) {
        fetchFailure = classifyFailure(error);
      },
      finish(requestedResult: HttpFinish) {
        if (finished) return;
        finished = true;
        const result = fetchFailure ?? requestedResult;
        record(
          {
            kind: "http",
            operationName: safeOperationName(operation.operationName),
            durationMs: clampDuration(now() - startedAt),
            result,
            request,
            response,
          },
          result === "completed" ? "info" : "warning",
        );
        if (result === "completed") {
          emit(
            "httpSuccess",
            "browser diagnostics: HTTP transport control",
            ["browser-connectivity-diagnostics", "http-control"],
            "http_control",
            random() < 0.01,
            {
              operationName: safeOperationName(operation.operationName),
              httpResult: result,
              endpoint: typeof request.endpoint === "string" ? request.endpoint : "unknown",
              responseStatus: response.status,
              responseChallenge: response.challenge,
            },
          );
        }
      },
    };
  }

  return {
    startRefresh,
    createRecoveryAttempt,
    observeRecoveryDecision,
    observeConnectivity,
    outageWarningContext: () => envelope("outage_warning"),
    startHttp,
  };
}

export function createDiagnosticHttpLink(args: {
  uri: string;
  diagnostics: BrowserConnectivityDiagnostics;
}): ApolloLink {
  return new ApolloLink((operation, forward) => {
    let requestDiagnostics: ReturnType<BrowserConnectivityDiagnostics["startHttp"]> | undefined;
    const httpLink = new HttpLink({
      uri: args.uri,
      credentials: "omit",
      fetch: async (input, init) => {
        try {
          requestDiagnostics = args.diagnostics.startHttp(operation, input, init);
        } catch {
          requestDiagnostics = undefined;
        }
        try {
          const response = await fetch(input, init);
          requestDiagnostics?.noteResponse(response);
          return response;
        } catch (error) {
          requestDiagnostics?.noteFetchFailure(error);
          throw error;
        }
      },
    });
    const downstream = httpLink.request(operation, forward);
    return new Observable((observer) => {
      const inner = downstream.subscribe({
        next: (result) => {
          // Record the parsed HTTP result before reachabilityLink publishes the
          // recovery transition, so the terminal incident includes its proof.
          requestDiagnostics?.finish("completed");
          observer.next(result);
        },
        error: (error) => {
          requestDiagnostics?.finish(
            operation.getContext().response instanceof Response &&
              operation.getContext().response.ok
              ? "parse_failure"
              : "http_error",
          );
          observer.error(error);
        },
        complete: () => {
          requestDiagnostics?.finish("completed");
          observer.complete();
        },
      });
      return () => {
        requestDiagnostics?.finish("cancelled");
        inner.unsubscribe();
      };
    });
  });
}

export function refreshFailureCategory(error: unknown): RefreshFinish {
  return classifyFailure(error);
}
